import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, ArrowLeft, MapPin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplyPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    diploma: "",
    location: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.diploma || !formData.location) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/applications/`, formData);
      setSubmitted(true);
      toast.success("Candidature envoyée avec succès !");
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const diplomas = [
    "BPJEPS Activités de la Forme",
    "DEJEPS Perfectionnement Sportif",
    "CQP Instructeur Fitness",
    "Licence STAPS",
    "Master STAPS",
    "Autre diplôme sportif"
  ];

  const locations = [
    { value: "Manosque", label: "Manosque", available: true },
    { value: "Gignac-la-Nerthe", label: "Gignac-la-Nerthe", available: true },
    { value: "Martigues", label: "Martigues", available: true }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#05050A]">
        <Header />
        <section className="min-h-screen flex items-center justify-center pt-32 pb-16" data-testid="success-section">
          <motion.div
            className="max-w-2xl w-full mx-auto px-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-10">
              <motion.div
                className="w-24 h-24 bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-full flex items-center justify-center mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-12 h-12 text-[#0066FF]" />
              </motion.div>

              <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
                Candidature reçue
              </span>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-black uppercase tracking-tight mb-4" data-testid="success-title">
                Merci {formData.first_name} !
              </h1>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                Florian va personnellement étudier votre profil et vous recontactera <span className="text-[#0066FF] font-bold">sous 48h</span>.
              </p>
            </div>

            <motion.div
              className="bg-[#0F0F16] border border-white/10 p-8 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              data-testid="next-steps-card"
            >
              <p className="text-[#0066FF] text-xs font-bold uppercase tracking-[0.2em] mb-6">
                Prochaines étapes
              </p>
              <ol className="space-y-4">
                {[
                  "Florian examine votre candidature",
                  "Premier appel de découverte (30 min)",
                  "Présentation de la méthode APM",
                  "Démarrage de l'accompagnement"
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#0066FF]/10 border border-[#0066FF]/30 flex items-center justify-center">
                      <span className="text-[#0066FF] font-bold text-sm">{i + 1}</span>
                    </div>
                    <span className="text-[#E2E8F0] leading-tight pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </motion.div>

            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <a
                href={`https://wa.me/33638134082?text=${encodeURIComponent(`Bonjour Florian, je viens de soumettre ma candidature APM (${formData.first_name} ${formData.last_name}).`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold uppercase tracking-wider py-4 px-6 transition-all"
                data-testid="whatsapp-contact-button"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Une question urgente ? Écrivez à Florian sur WhatsApp</span>
              </a>
              <p className="text-center text-xs text-[#64748B] mt-2">
                Réponse rapide en journée · +33 6 38 13 40 82
              </p>
            </motion.div>

            <motion.div
              className="bg-[#0066FF]/5 border-l-2 border-[#0066FF] p-4 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                <span className="text-white font-semibold">Email de confirmation</span> envoyé à{" "}
                <span className="text-[#0066FF]">{formData.email}</span>. Pensez à vérifier votre dossier &laquo;&nbsp;Promotions&nbsp;&raquo; ou &laquo;&nbsp;Spam&nbsp;&raquo;.
              </p>
            </motion.div>

            <div className="text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#3B82F6] font-semibold transition-colors"
                data-testid="back-home-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à l&apos;accueil
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="apply-hero">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00dc70e4-70e9-471a-a527-cf5ef1f5c6b5/images/f2cd200b3db4fc136cc5daabd7828ff2fb8403cc5bd88569b0aeef2fd67a6f97.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Candidature
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6">
              Rejoignez<br />
              <span className="text-[#0066FF]">APM</span>
            </h1>
            <p className="text-[#94A3B8] text-lg leading-relaxed">
              Remplissez le formulaire ci-dessous pour postuler et atteindre 
              <span className="text-[#0066FF]"> 3000€/mois en 90 jours</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding" data-testid="apply-form-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-2xl mx-auto"
            {...fadeInUp}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="apply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="first_name" className="form-label">
                    Prénom *
                  </Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    className="form-input bg-[#0F0F16] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="form-label">
                    Nom *
                  </Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="form-input bg-[#0F0F16] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                    required
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="form-label">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  className="form-input bg-[#0F0F16] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                  required
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="form-label">
                  Téléphone *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+33 6 12 34 56 78"
                  className="form-input bg-[#0F0F16] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                  required
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="diploma" className="form-label">
                  Diplôme sportif *
                </Label>
                <Select onValueChange={(value) => handleSelectChange('diploma', value)} value={formData.diploma}>
                  <SelectTrigger 
                    className="form-input bg-[#0F0F16] border-white/10 text-white h-12"
                    data-testid="select-diploma"
                  >
                    <SelectValue placeholder="Sélectionnez votre diplôme" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F16] border-white/10">
                    {diplomas.map((diploma) => (
                      <SelectItem 
                        key={diploma} 
                        value={diploma}
                        className="text-white hover:bg-[#0066FF]/20 focus:bg-[#0066FF]/20"
                      >
                        {diploma}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location" className="form-label flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#0066FF]" />
                  Salle Basic-Fit souhaitée *
                </Label>
                <Select onValueChange={(value) => handleSelectChange('location', value)} value={formData.location}>
                  <SelectTrigger 
                    className="form-input bg-[#0F0F16] border-white/10 text-white h-12"
                    data-testid="select-location"
                  >
                    <SelectValue placeholder="Choisissez votre salle" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F16] border-white/10">
                    {locations.map((loc) => (
                      <SelectItem 
                        key={loc.value} 
                        value={loc.value}
                        className="text-white hover:bg-[#0066FF]/20 focus:bg-[#0066FF]/20"
                      >
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="form-label">
                  Message (optionnel)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de votre parcours et de vos motivations..."
                  rows={5}
                  className="form-input bg-[#0F0F16] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] resize-none"
                  data-testid="input-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer ma candidature
                  </>
                )}
              </button>

              <p className="text-center text-sm text-[#64748B]">
                Florian vous recontactera sous 48h après réception de votre candidature
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplyPage;
